import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { clsx } from 'clsx'

import {
  MAX_SEO_DESCRIPTION_LENGTH_SUGGESTION,
  MAX_SEO_TITLE_LENGTH,
  MIN_SEO_DESCRIPTION_LENGTH_SUGGESTION,
} from '@bettermode/common/meta-tags'
import { Image, NetworkVisibility } from '@tribeplatform/gql-client/types'
import {
  useNetwork,
  usePage,
  useUpdateSpace,
} from '@tribeplatform/react-sdk/hooks'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Divider } from '@tribeplatform/react-ui-kit/Divider'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { SectionHeader } from '@tribeplatform/react-ui-kit/SectionHeader'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { PlanBadge } from '../../common/components/PlanBadge.js'
import {
  RECOMMENDED_WIDTH,
  RECOMMENDED_HEIGHT,
  SeoImagePicker,
} from '../../common/components/SeoImagePicker/index.js'
import { T } from '../../i18n/components/T.js'
import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { Link } from '../../Link/Link.js'
import {
  SearchEngineResultPreviewCard,
  OpenGraphPreviewCard,
} from '../../SeoCards/index.js'

import { NoIndexWarnings } from './Components/NoIndexWarnings.js'
import { SitemapWarnings } from './Components/SitemapWarnings.js'
import { SpaceSettingsProps } from './types.js'

// TODO: Merge with TitleHelperText in `packages/web/src/client/components/Admin/Settings/SeoSettings/TitleHelperText.tsx`
const TitleHelperText = ({ value }) => (
  <div className="grid grid-cols-6 text-content-subdued">
    <span className="col-span-5">
      <T
        defaultMessage="Keep your title concise and under {length, number} characters for best search engine visibility."
        id="Generics.RecommendedLength"
        values={{
          length: MAX_SEO_TITLE_LENGTH,
        }}
      />
    </span>

    <span
      className={clsx(
        'text-bold col-span-1 flex justify-end',
        // eslint-disable-next-line no-nested-ternary
        value
          ? value.length > MAX_SEO_TITLE_LENGTH
            ? 'text-content-on-attention'
            : 'text-content-on-positive'
          : '',
      )}
    >
      <T
        defaultMessage="Length: {length, number}"
        id="Generics.LengthIndicator"
        values={{
          length: value?.length || 0,
        }}
      />
    </span>
  </div>
)

// TODO: Merge with DescriptionHelperText in `packages/web/src/client/components/Admin/Settings/SeoSettings/DescriptionHelperText.tsx`
const DescriptionHelperText = ({ value }) => (
  <div className="grid grid-cols-6 text-content-subdued">
    <span className="col-span-5">
      <T
        defaultMessage="Write a description of {min_length, number} - {max_length, number} characters to give search engines a clear summary of your content."
        id="Generics.RecommendedLengthRange"
        values={{
          min_length: MIN_SEO_DESCRIPTION_LENGTH_SUGGESTION,
          max_length: MAX_SEO_DESCRIPTION_LENGTH_SUGGESTION,
        }}
      />
    </span>
    <span
      className={clsx(
        'text-bold col-span-1 flex justify-end',
        // eslint-disable-next-line no-nested-ternary
        value
          ? value.length > MAX_SEO_DESCRIPTION_LENGTH_SUGGESTION ||
            value.length < MIN_SEO_DESCRIPTION_LENGTH_SUGGESTION
            ? 'text-content-on-attention'
            : 'text-content-on-positive'
          : '',
      )}
    >
      <T
        defaultMessage="Length: {length, number}"
        id="Generics.LengthIndicator"
        values={{
          length: value?.length || 0,
        }}
      />
    </span>
  </div>
)

export const SpaceSeoSettings = ({
  space,
  onClose,
  isChangingTab,
  confirmTabChange,
  rejectTabChange,
  setDirty,
  setSubmit,
  isActive,
}: SpaceSettingsProps) => {
  const { $t } = useI18n()
  const { data: network, isPlaceholderData: isNetworkLoading } = useNetwork({
    fields: {
      limits: 'all',
      customSeoDetail: 'all',
    },
  })
  const customSeoDetail = space?.customSeoDetail
  const isPrivateNetwork = network?.visibility === NetworkVisibility.private
  const isNetworkNoIndex = !!network?.customSeoDetail?.noIndex
  const isPrivateSpace = !!space?.private
  const isSpaceNoindex = !!customSeoDetail?.noIndex
  const isSitemapEnabled = !!network?.limits?.usageLimits?.sitemap
  const sitemapHasWarning =
    isPrivateNetwork || isNetworkNoIndex || isPrivateSpace || isSpaceNoindex
  const { mutateAsync: updateSpace, isLoading } = useUpdateSpace()
  const { data: page } = usePage({
    variables: {
      path: space.address?.path,
    },
    useQueryOptions: {
      cacheTime: 0,
      enabled: !!space.address?.path,
    },
  })
  const buttonRef = useRef<HTMLButtonElement>()

  const initialCustomSeoDetail = useMemo(
    () => ({
      title: space.customSeoDetail.title,
      description: space.customSeoDetail.description,
      noIndex: space.customSeoDetail.noIndex,
      thumbnail: space.customSeoDetail.thumbnail as Image,
    }),
    [space],
  )

  const [customFields, setCustomFields] = useState<{
    title?: string
    description?: string
    noIndex?: boolean
    thumbnail?: Image
  }>(initialCustomSeoDetail)

  useEffect(() => {
    if (isActive) {
      setSubmit(() => () => buttonRef?.current?.click())
    }
  }, [isActive, setSubmit, buttonRef])

  useEffect(() => {
    if (isActive) {
      setDirty(customFields !== initialCustomSeoDetail)
    }
  }, [isActive, setDirty, customFields, initialCustomSeoDetail])

  const setField = useCallback(
    (key, value) => {
      setCustomFields({
        ...customFields,
        [key]: value,
      })
    },
    [customFields, setCustomFields],
  )

  const onSeoImageChange = useCallback(
    (image?: Image) => {
      setField('thumbnail', image)
    },
    [setField],
  )

  const onSubmit = async () => {
    try {
      await updateSpace({
        id: space.id,
        input: {
          customSeoDetail: {
            title: customFields.title,
            description: customFields.description,
            noIndex: customFields.noIndex,
            thumbnailId: customFields.thumbnail?.id,
          },
        },
      })
      toast({
        title: $t({
          defaultMessage: '{SPACE_CC} SEO settings updated',
          id: 'Admin.Space.SeoSettings.Updated',
        }),
        status: 'success',
      })
      if (isChangingTab) {
        confirmTabChange()
      } else {
        onClose()
      }
    } catch (e) {
      toast({
        title: $t({
          defaultMessage: 'Unable to update {SPACE} SEO settings',
          id: 'Admin.Space.SeoSettings.FailedToUpdate',
        }),
        status: 'error',
      })
      if (isChangingTab) {
        rejectTabChange()
      }
    }
  }

  const previewProps = {
    title: customFields.title || page?.seoDetail?.title,
    description: customFields.description || page?.seoDetail?.description,
    thumbnailUrl:
      customFields.thumbnail?.url || (page?.seoDetail?.thumbnail as Image)?.url,
  }

  return (
    <div className="sm:h-132 flex flex-col">
      <div className="flex flex-col flex-grow overflow-auto p-5">
        <SectionHeader
          title={$t({
            defaultMessage: 'Search engine optimization',
            id: 'Admin.SeoSettings.Seo',
          })}
          description={$t({
            id: 'Admin.SeoSettings.Description',
            defaultMessage:
              'Enhance visibility by customizing SEO settings, including meta titles, descriptions, and Open Graph image for better search and social media presence.',
          })}
          className="pb-5"
        />
        <div className="space-y-5">
          {space.isHomepage && (
            <Alert status="info">
              <div className="flex flex-col gap-1">
                <div>
                  <T
                    defaultMessage="This {SPACE} is currently set as your homepage. Its custom SEO settings will apply to its <SpacePageLink>direct URL</SpacePageLink>, but the <HomepageLink>homepage URL</HomepageLink> will always use the network's SEO settings."
                    id="Admin.Space.SeoSettings.HomepageOverrideWarning.Main"
                    values={{
                      SpacePageLink: chunks => {
                        return (
                          <Link
                            variant="neutral"
                            href={`/${space.address.path}`}
                            target="_blank"
                          >
                            <span>{chunks}</span>
                            <SvgIcon
                              name="link-external"
                              inline
                              className="ms-1"
                            />
                          </Link>
                        )
                      },
                      HomepageLink: chunks => {
                        return (
                          <Link variant="neutral" href="/" target="_blank">
                            <span>{chunks}</span>
                            <SvgIcon
                              name="link-external"
                              inline
                              className="ms-1"
                            />
                          </Link>
                        )
                      },
                    }}
                  />
                </div>
                <div>
                  <T
                    defaultMessage="For homepage SEO settings, please refer to the <CommunitySettings>Community SEO settings</CommunitySettings>."
                    id="Admin.Space.SeoSettings.HomepageOverrideWarning.Footer"
                    values={{
                      CommunitySettings: chunks => {
                        return (
                          <Link variant="neutral" href="/manage/settings/seo">
                            {chunks}
                          </Link>
                        )
                      },
                    }}
                  />
                </div>
              </div>
            </Alert>
          )}

          <FormControl.Input
            value={customFields.title}
            onChange={e => {
              setField('title', e.target.value)
            }}
            label={
              <T defaultMessage="Meta title" id="Admin.SeoSettings.MetaTitle" />
            }
            name="title"
            placeholder={$t({
              defaultMessage:
                'Add a meta title. If blank, the {SPACE} name is used',
              id: 'Admin.Space.SeoSettings.TitlePlaceholder',
            })}
            helperText={<TitleHelperText value={customFields.title} />}
          />
          <FormControl.Textarea
            name="description"
            label={
              <T
                defaultMessage="Meta description"
                id="Admin.SeoSettings.MetaDescription"
              />
            }
            placeholder={$t({
              defaultMessage:
                'Add a meta description. If blank, the {SPACE} description is used by default',
              id: 'Admin.Space.SeoSettings.DescriptionPlaceholder',
            })}
            value={customFields.description}
            onChange={e => {
              setField('description', e.target.value)
            }}
            helperText={
              <DescriptionHelperText value={customFields.description} />
            }
          />
          <div>
            <SeoImagePicker
              label={
                <FormControl.Label>
                  <T
                    defaultMessage="Open Graph Image"
                    id="Admin.Space.SeoSettings.OpenGraphImage"
                  />
                </FormControl.Label>
              }
              image={customFields.thumbnail}
              onChange={onSeoImageChange}
            />
            <FormControl.HelperText className="mt-2">
              <T
                id="Admin.SeoSettings.OpenGraphImage.Description"
                defaultMessage="Choose a banner image for SEO cards and social media previews ({width} x {height} px recommended)."
                values={{
                  width: RECOMMENDED_WIDTH,
                  height: RECOMMENDED_HEIGHT,
                }}
              />
            </FormControl.HelperText>
          </div>

          <Divider />

          <div className="flex flex-col space-y-3">
            <FormControl.Toggle
              label={$t({
                defaultMessage: 'Hide from search results',
                id: 'Admin.Space.SeoSettings.Hide',
              })}
              helperText={$t({
                defaultMessage:
                  'Enable this option to prevent search engines from indexing the {SPACE} and its posts. This will also exclude the {SPACE} from the sitemap.',
                id: 'Admin.Space.SeoSettings.HideSeo.Description',
              })}
              name="noIndex"
              checked={customFields.noIndex}
              disabled={isPrivateNetwork || isPrivateSpace || isNetworkNoIndex}
              onChange={() => {
                setField('noIndex', !customFields.noIndex)
              }}
            />
            <NoIndexWarnings
              isPrivateNetwork={isPrivateNetwork}
              isPrivateSpace={isPrivateSpace}
              isNetworkNoIndex={isNetworkNoIndex}
            />

            <Divider />

            {!isNetworkLoading && (
              <div className="flex flex-col gap-2">
                <FormControl.Label className="flex items-center gap-2">
                  <T defaultMessage="Sitemap" id="Admin.SeoSettings.Sitemap" />
                  <PlanBadge authorized={isSitemapEnabled} size="md" />
                </FormControl.Label>
                {!isSitemapEnabled && (
                  <Alert status="info">
                    <T
                      defaultMessage="Boost your SEO and drive more traffic to your site with automatic sitemap generation, available on our paid plans. Without a sitemap, your site may struggle to be found by search engines. Upgrade today to unlock this essential feature and ensure your content is easily discoverable. <link>Upgrade now.</link>"
                      id="Admin.SeoSettings.Sitemap.UpSellDescription"
                      values={{
                        link: chunks => {
                          return (
                            <Link
                              href="/manage/billing/plans"
                              variant="accent"
                              target="_blank"
                            >
                              {chunks}
                            </Link>
                          )
                        },
                      }}
                    />
                  </Alert>
                )}
                <FormControl.HelperText>
                  {isSitemapEnabled && (
                    <p>
                      <T
                        defaultMessage="A sitemap lists all posts in the {SPACE} to help search engines index your content. It’s auto-generated unless indexing is disabled."
                        id="Admin.Space.Sitemap.Description"
                      />
                    </p>
                  )}
                  {!sitemapHasWarning && isSitemapEnabled && (
                    <p>
                      <Link
                        href={`/${space.address.path}/sitemap.xml`}
                        variant="accent"
                        target="_blank"
                        className="inline-flex gap-1 items-center"
                      >
                        <T
                          id="Admin.Space.Sitemap.ClickToOpen"
                          defaultMessage="View {SPACE} sitemap.xml"
                        />
                        <SvgIcon name="link-external" inline />
                      </Link>
                    </p>
                  )}
                </FormControl.HelperText>
                <SitemapWarnings
                  isPrivateNetwork={isPrivateNetwork}
                  isPrivateSpace={isPrivateSpace}
                  isSpaceNoindex={isSpaceNoindex}
                  isNetworkNoIndex={isNetworkNoIndex}
                />
              </div>
            )}
          </div>
        </div>
        <Divider padding="none" className="my-5" />
        <div>
          <h6 className="text-xs font-medium text-content-subdued mb-1">
            <T
              defaultMessage="Search Result Preview"
              id="Admin.Space.SeoSettings.SearchResultPreview"
            />
          </h6>
          <SearchEngineResultPreviewCard {...previewProps} />
          <h6 className="text-xs font-medium text-content-subdued mt-3 mb-1">
            <T
              defaultMessage="Open Graph Preview"
              id="Admin.Space.SeoSettings.OpenGraphPreview"
            />
          </h6>
          <OpenGraphPreviewCard {...previewProps} />
        </div>
      </div>
      <div className="flex justify-end px-5 py-5 border-t space-s-2">
        <Button
          type="button"
          variant="secondaryNeutral"
          onClick={() => onClose()}
        >
          <T defaultMessage="Cancel" id="Generics.Cancel" />
        </Button>
        <Button
          type="submit"
          loading={isLoading}
          onClick={onSubmit}
          ref={buttonRef}
        >
          <T defaultMessage="Update" id="Generics.Update" />
        </Button>
      </div>
    </div>
  )
}
